import React, { useEffect, useMemo } from "react";
import { Section } from "@/shared/ui/Blocks";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { Navigate } from "react-router";
import { map, take, times } from "lodash";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Column, Row } from "@/shared/ui/Blocks";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { SubtitleText } from "@/shared/ui/Typography";
import { useViewer } from "@/features/workspace/hooks/useViewer";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import useOrgNavigate, {
  buildOrgRoute,
} from "@/shared/utils/router/useOrgNavigate";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { RoleTypes } from "@/shared/constants";
import { useLearningCenter } from "@/features/organization/hooks/useLearningCenter";
import ResourceCard from "@/shared/ui/ResourceCard";
import useProductNewsFeed from "@/shared/hooks/content/useProductNewsFeed";
import NewsFeedItem from "@/shared/ui/NewsFeedItem";
import useOnboarding from "@/features/profile/hooks/useOnboarding";
import { OnboardingTask } from "@/lib/onboarding/tasks-map";
import { OnboardingActions } from "@/shared/widgets/OnboardingActions";
import useIntercom from "@/lib/intercom/useIntercom";
import { getPersonRole } from "@/shared/utils/selectors/getRolesAndAccess";
import { ResourceEnum } from "@/shared/constants/resources";
import { Skeleton } from "@/shared/ui";

function Dashboard() {
  const { viewer } = useViewer();
  const { account } = useAccount();
  const { t } = useTranslation();
  const navigate = useOrgNavigate();
  const { updateIntercom } = useIntercom();
  const { organization } = useOrganisation();

  const { handleEmitOnboardingStep } = useOnboarding();

  const { resources } = useLearningCenter();

  const { newsFeed, loading: loadingNewsFeed } = useProductNewsFeed();

  const viewerRole = useMemo(() => {
    const viewerOrgRole = getPersonRole(
      viewer.person,
      ResourceEnum.organization,
      organization.id!
    );

    if (viewerOrgRole) {
      const role = RoleTypes.find((r) => r.role === viewerOrgRole);
      return role?.name;
    }

    return "Member";
  }, [organization, viewer.person?.roles]);

  useEffect(() => {
    updateIntercom({
      custom_attributes: {
        role: viewerRole,
      },
    });
  }, [viewerRole]);

  // When the account has a custom landing route, redirect to it
  if (account.config?.landing_route) {
    const landingRoute = buildOrgRoute(account.config?.landing_route);
    if (landingRoute) {
      return (
        <Navigate
          to={{
            pathname: landingRoute,
            search: location.search,
          }}
        />
      );
    }
  }

  if (!viewer.id) {
    return null;
  }

  // // Redirect to persona select if the user is a new account
  // if (
  //   viewer?.onboarding?.type === "new_account" &&
  //   !viewer?.onboarding?.completed_at
  // ) {
  //   return <Navigate to="/onboarding" />;
  // }

  return (
    <ScreenContainer className="overflow-y-auto">
      <Section>
        <Row className="gap-4 md:gap-8 transition-all duration-300 flex-col md:flex-row">
          <AnimatePresence mode="popLayout">
            <Row
              className="px-4 py-8 flex-1 gap-4 items-center border border-gray-200 rounded-xl max-h-[160px] cursor-pointer"
              onClick={() => {
                navigate("/profile");
              }}
            >
              <motion.div
                key="viewer-avatar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AvatarWithInitials
                  size="lg"
                  initials={viewer.initials || ""}
                  avatar={viewer.avatar_url || ""}
                />
              </motion.div>
              <Column>
                <motion.h3
                  key="viewer-name"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  Welcome back, {viewer.first_name}! 👋
                </motion.h3>
                {!!viewer.person && (
                  <motion.div
                    key="viewer-role"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <SubtitleText>
                      You are currently a {viewerRole} of {organization.name}
                    </SubtitleText>
                  </motion.div>
                )}
              </Column>
            </Row>
          </AnimatePresence>
          <Column className="flex-1">
            {/* <DashboardActions organization={organization as OrganizationType} /> */}
            <OnboardingActions />
          </Column>
        </Row>
      </Section>

      <Section className="md:mt-16">
        <SectionHeader
          title="Recommended resources"
          subtitle="Learn about AssessioAI app and get the most out if it."
        />
        <Row className="gap-4 flex-wrap">
          {map(resources, (resource) => (
            <ResourceCard
              key={resource.id}
              {...resource}
              title={t(resource.title_key)}
              onClick={() => {
                handleEmitOnboardingStep(OnboardingTask.DISCOVER_RESOURCES);
                window.open(resource.url, "_blank");
              }}
            />
          ))}
        </Row>
      </Section>

      <Section>
        <SectionHeader
          title="Product news"
          subtitle="Stay up to date with the latest product news."
        />
        {!loadingNewsFeed && newsFeed.length > 0 && (
          <Row className="gap-4 flex-wrap">
            {map(take(newsFeed, 5), (post) => (
              <NewsFeedItem key={post.url} {...post} />
            ))}
          </Row>
        )}
        {loadingNewsFeed && (
          <Row className="gap-4 flex-wrap">
            {times(5, (index) => (
              <Skeleton
                key={index}
                className="h-[150px] w-[200px] flex-col p-4 bg-grey-100 rounded-lg"
              >
                <Skeleton className="w-[36px] h-[35px] rounded-full bg-grey-200" />
                <Skeleton className="w-2/3 h-4 mt-4 bg-grey-200" />
                <Skeleton className="w-3/4 h-2 mt-4 bg-grey-200" />
                <Skeleton className="w-3/5 h-2 mt-4 bg-grey-200" />
              </Skeleton>
            ))}
          </Row>
        )}
      </Section>
    </ScreenContainer>
  );
}

export default Dashboard;
